import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Globe, Clock, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import api, { formatApiError } from "../lib/api";
import MarketingLayout from "../components/marketing/MarketingLayout";
import { LineMask, FadeIn, SectionTag } from "../components/marketing/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", clinic: "", country: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      toast.success("Enquiry sent — the team will reply within one business day.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <MarketingLayout>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
        <section className="pt-40 pb-24 lg:pb-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <SectionTag>Contact</SectionTag>
              <h1 className="font-display font-black tracking-tight leading-[0.98] text-[clamp(2.4rem,5vw,4.5rem)]">
                <LineMask delay={0.1}>Talk to a</LineMask>
                <LineMask delay={0.25} className="text-neon">planning lead.</LineMask>
              </h1>
              <FadeIn delay={0.5}>
                <p className="mt-6 text-slate-400 text-sm md:text-base leading-relaxed max-w-md">
                  Volume pricing, white-label workflows, platform integration or just a first case — tell us about your practice and we'll respond within one business day.
                </p>
                <ul className="mt-10 space-y-5 text-sm">
                  <li className="flex items-center gap-3 text-slate-300"><Mail className="h-4 w-4 text-neon" /> hello@orthoplanix.com</li>
                  <li className="flex items-center gap-3 text-slate-300"><Globe className="h-4 w-4 text-neon" /> USA · UK · Canada · Australia · NZ · UAE · KSA · Europe · SEA</li>
                  <li className="flex items-center gap-3 text-slate-300"><Clock className="h-4 w-4 text-neon" /> Response within one business day</li>
                </ul>
              </FadeIn>
            </div>

            <div className="lg:col-span-7">
              <FadeIn delay={0.3}>
                {sent ? (
                  <div className="bg-white/5 border border-neon/30 rounded-2xl p-12 text-center" data-testid="contact-success">
                    <CheckCircle2 className="h-12 w-12 text-neon mx-auto mb-6" />
                    <h2 className="font-display font-bold text-2xl">Enquiry received.</h2>
                    <p className="mt-3 text-sm text-slate-400">The OrthoPlaniX team will get back to you shortly. Check your inbox for confirmation.</p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 lg:p-10 space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="c-name" className="text-slate-300">Full name *</Label>
                        <Input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")}
                          className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="Dr. Jane Smith" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c-email" className="text-slate-300">Work email *</Label>
                        <Input id="c-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={set("email")}
                          className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="jane@clinic.com" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c-clinic" className="text-slate-300">Clinic / Company</Label>
                        <Input id="c-clinic" data-testid="contact-clinic-input" value={form.clinic} onChange={set("clinic")}
                          className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="Smile Studio Dental" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="c-country" className="text-slate-300">Country</Label>
                        <Input id="c-country" data-testid="contact-country-input" value={form.country} onChange={set("country")}
                          className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="United Kingdom" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c-subject" className="text-slate-300">Subject</Label>
                      <Input id="c-subject" data-testid="contact-subject-input" value={form.subject} onChange={set("subject")}
                        className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="Lab partnership / volume pricing / first case…" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="c-message" className="text-slate-300">Message *</Label>
                      <Textarea id="c-message" data-testid="contact-message-input" required rows={5} value={form.message} onChange={set("message")}
                        className="bg-ink/60 border-white/15 text-white placeholder:text-slate-600 focus-visible:ring-neon" placeholder="Tell us about your case volume, platforms and goals…" />
                    </div>
                    <Button type="submit" disabled={sending} data-testid="contact-submit-btn"
                      className="w-full sm:w-auto rounded-full bg-neon text-ink font-bold hover:bg-neon/90 px-8 py-6 text-sm transition-transform hover:scale-[1.02]">
                      {sending ? "Sending…" : "Send enquiry"} <ArrowUpRight className="h-4 w-4 ml-2" />
                    </Button>
                  </form>
                )}
              </FadeIn>
            </div>
          </div>
        </section>
      </motion.div>
    </MarketingLayout>
  );
}
